import {
  ButtonContainer,
  FieldLabel,
  FieldWrapper,
  Row,
  StyledCustomSelect,
  StyledForm,
} from './DetailsPage.styled';
import Wrapper from '../Wrapper/Wrapper';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import Button from 'components/Button/Button';
import { Field, Formik, FormikProps } from 'formik';
import { Input } from 'components/Input/InputFormik';
import { detailsCompanyValidationSchema } from './validation';
import PageNotFound from 'pages/PageNotFound/PageNotFound';
import { useRegisterCompany } from './hooks';
import { useHandleLoginSuccess } from 'utils/hooks/useHandleLoginSuccess';
import { useGetCountryOptions } from 'utils/hooks/useGetCountryOptions';
import { IOption } from 'components/Select/type';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';

const DetailsPage = () => {
  const formikRef = useRef<FormikProps<any> | any>();
  const { t } = useTranslation();
  const { isPhone } = useBreakpointFlag();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const lang = searchParams.get('lang');
  const companyName = searchParams.get('companyName');
  const userId = searchParams.get('userId');
  const countryOptions: IOption[] = useGetCountryOptions();

  usePageTitle('Company details');

  if (!token) {
    return <PageNotFound />;
  }

  const {
    data,
    mutate: createNewAccount,
    isLoading,
  } = useRegisterCompany(() =>
    navigate(
      RoutesConfig.CompanyLogin.buildPath(
        token,
        lang || 'en',
        companyName || ''
      )
    )
  );

  useHandleLoginSuccess(data);

  const handleSubmit = () => {
    const { name, email, street, house_number, zip_code, city, country } =
      formikRef.current.values;

    const address = {
      street,
      house_number: house_number === '' ? null : house_number,
      zip_code,
      city,
      country,
    };

    createNewAccount({
      token,
      userId: Number(userId),
      name,
      email,
      address,
    });
  };

  return (
    <Wrapper title={t('Company details')}>
      <Formik
        innerRef={formikRef}
        initialValues={{
          name: companyName,
          email: '',
          street: '',
          house_number: '',
          zip_code: '',
          city: '',
          country: '',
        }}
        validationSchema={detailsCompanyValidationSchema}
        validateOnMount
        onSubmit={() => {
          //
        }}
      >
        {({ setFieldValue, errors, touched, isValid }) => {
          return (
            <>
              <StyledForm>
                <Row>
                  <FieldWrapper>
                    <FieldLabel>{t('Company name')}</FieldLabel>
                    <Field
                      name="name"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) => setFieldValue('name', str)}
                      wrapperStyles={{ width: '100%' }}
                      errorMessage={
                        touched['name'] && errors['name'] ? errors['name'] : ''
                      }
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FieldLabel>{t('Company Email')}</FieldLabel>
                    <Field
                      name="email"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) => setFieldValue('email', str)}
                      wrapperStyles={{ width: '100%' }}
                      errorMessage={
                        touched['email'] && errors['email']
                          ? errors['email']
                          : ''
                      }
                    />
                  </FieldWrapper>
                </Row>
                <Row>
                  <FieldWrapper>
                    <FieldLabel>{t('Street')}</FieldLabel>
                    <Field
                      name="street"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) => setFieldValue('street', str)}
                      wrapperStyles={{ width: '100%' }}
                      errorMessage={
                        touched['street'] && errors['street']
                          ? errors['street']
                          : ''
                      }
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FieldLabel>{t('House number')}</FieldLabel>
                    <Field
                      name="house_number"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) =>
                        setFieldValue('house_number', str)
                      }
                      wrapperStyles={{ width: '100%' }}
                    />
                  </FieldWrapper>
                </Row>
                <Row>
                  <FieldWrapper>
                    <FieldLabel>{t('Zip code')}</FieldLabel>
                    <Field
                      name="zip_code"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) => setFieldValue('zip_code', str)}
                      wrapperStyles={{ width: '100%' }}
                      errorMessage={
                        touched['zip_code'] && errors['zip_code']
                          ? errors['zip_code']
                          : ''
                      }
                    />
                  </FieldWrapper>
                  <FieldWrapper>
                    <FieldLabel>{t('City')}</FieldLabel>
                    <Field
                      name="city"
                      as={Input}
                      height={'42rem'}
                      onChange={(str: string) => setFieldValue('city', str)}
                      wrapperStyles={{ width: '100%' }}
                      errorMessage={
                        touched['city'] && errors['city'] ? errors['city'] : ''
                      }
                    />
                  </FieldWrapper>
                </Row>
                <Row>
                  <FieldWrapper>
                    <FieldLabel>{t('Country')}</FieldLabel>
                    <Field
                      errorMessage={
                        touched['country'] && errors['country']
                          ? errors['country']
                          : ''
                      }
                      disabled={false}
                      height={'41rem'}
                      name="country"
                      useFilterOption
                      options={countryOptions}
                      component={StyledCustomSelect}
                      isMulti={false}
                      onSelect={(option: IOption) => {
                        setFieldValue('country', option.value);
                      }}
                    />
                  </FieldWrapper>
                </Row>
              </StyledForm>

              <ButtonContainer>
                <Button
                  label={t('Continue')}
                  primary
                  disabled={isLoading || !isValid}
                  width={isPhone ? '100%' : '580rem'}
                  height={'42rem'}
                  onClick={() => handleSubmit()}
                />
              </ButtonContainer>
            </>
          );
        }}
      </Formik>
    </Wrapper>
  );
};

export default DetailsPage;
